import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { SeriesDataPoint, WidgetType, WidgetWithData } from '@models/widget/widget.model';
import { WIDGET_NUMBER_DEFAULT_COLOR } from '@models/widget/widget.constants';

@Component({
  standalone: false,
  selector: 'app-widget-number',
  templateUrl: './widget-number.component.html',
  styleUrls: ['./widget-number.component.scss'],
})
export class WidgetNumberComponent {
  @Input({ required: true }) widget!: WidgetWithData;
  @Input() showChrome = false;
  @Input() typeLabel = '';
  @Input() sourceLabel = '';
  @Input() isEditable = false;
  @Input() canDelete = true;
  @Input() isEditMode = false;
  @Input() displayType: WidgetType = 'NUMBER';
  @Input() isMockPreview = false;
  @Input() sensorTypeLabels: string[] = [];

  @Output() edit   = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  private readonly defaultDecimals = 1;
  private readonly datePipe = new DatePipe('es-ES');

  get primaryColor(): string {
    return this.widget.entries?.[0]?.color || (this.displayType === 'NUMBER' ? WIDGET_NUMBER_DEFAULT_COLOR : '#6b7280');
  }

  get valueColor(): string {
    return this.hasValue ? this.primaryColor : '#b0b8c4';
  }

  get hasValue(): boolean {
    return this.lastPoint !== null;
  }

  get unit(): string { return this.widget.entries?.[0]?.unit || ''; }

  get displayLabel(): string {
    const entry = this.widget.entries?.[0];
    const label = (entry?.label ?? '').trim();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
  }

  get value(): string {
    const point = this.lastPoint;
    if (!point) return '--';
    const raw = point.value;
    if (typeof raw === 'string') {
      const asDate = new Date(raw);
      if (!isNaN(asDate.getTime()) && raw.includes('-')) {
        return this.datePipe.transform(asDate, 'dd MMM yyyy') ?? raw;
      }
      return raw;
    }
    return raw.toFixed(this.defaultDecimals);
  }

  get isTextValue(): boolean {
    const point = this.lastPoint;
    if (!point) return false;
    const raw = point.value;
    if (typeof raw !== 'string') return false;
    const asDate = new Date(raw);
    if (!isNaN(asDate.getTime()) && raw.includes('-')) return false;
    return isNaN(Number(raw));
  }

  get isAreaPropertyValue(): boolean {
    return this.widget.entries?.[0]?.dataSourceType === 'AREA_PROPERTY';
  }

  get isDeviceValue(): boolean {
    return this.widget.entries?.[0]?.dataSourceType === 'DEVICE';
  }

  get isLongAreaPropertyValue(): boolean {
    return this.isAreaPropertyValue && this.value.length > 8;
  }

  get isVeryLongAreaPropertyValue(): boolean {
    return this.isAreaPropertyValue && this.value.length > 14;
  }

  get valueTooltip(): string | null {
    return this.hasValue && this.isAreaPropertyValue ? this.value : null;
  }

  get lastDate(): string {
    const entry = this.widget.entries?.[0];
    if (entry?.dataSourceType === 'AREA_PROPERTY') return '';
    return this.lastPoint?.date ?? '';
  }

  private get lastPoint(): SeriesDataPoint | null {
    const data = this.widget.entries?.[0]?.data ?? [];
    return data.length ? data[data.length - 1] : null;
  }
}
